import t from 'react-translate';
import { useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import { AngularServicesContext } from 'react-app';
import { RootState } from 'redux/schemas';
import { useAppDispatch } from 'redux/store';
import { LecturePage } from 'redux/schemas/models/lecture-page';
import { CourseAliases } from 'redux/schemas/models/course';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { createLecturePage, deleteLecturePage, duplicateLecturePage } from 'redux/actions/lecture-pages';
import { CannotBeCopiedComponentsMeta, getCannotBeCopiedComponentsMeta, getNearestLecture } from 'redux/selectors/lecture-page';
import { getCourseAliases, getCurrentCourse, getCurrentCourseCourseLongTeamSet, getCurrentCourseGroupTeamSet, getFlatCourseAliases } from 'redux/selectors/course';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { useCourseOutline } from 'redux/selectors/course-outline/use-course-outline';
import { unsetCurrentCourseCourseLongTeamSet, unsetCurrentCourseGroupTeamSet } from 'redux/actions/lecture-components';
import { LecturePageMode } from 'lecture_pages/components';
import { getFullCourseTimelineWithProgress } from 'redux/actions/timeline';
import { createLecturePage as createCollectionLecturePage, getCanLinkCollectionLesson, getCanConvertToCollectionLesson, canConvertToCollectionLecture } from 'redux/actions/collections';
import { hideAddUI } from 'lecture_pages/templates/components/nv-add-component';
import { getLecturePage } from 'redux/selectors/timeline';
import { useEnterLecturePageMode, useLecturePageFromParams, useLecturePageLink, useLecturePageParams } from './lecture-routing';

export const useHandleReorder = () => {
  const enterMode = useEnterLecturePageMode();
  const params = useLecturePageParams();

  return useCallback(() => {
    enterMode(
      LecturePageMode.REORDER,
      params.mode === LecturePageMode.EDIT || params.mode === LecturePageMode.RESTRICTED_EDIT,
    );
  }, [enterMode, params.mode]);
};

export const useDuplicateLecture = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const aliases: CourseAliases = useSelector((state: RootState) => getCourseAliases(state));
  const lecturePage = useSelector((state) => getLecturePage(state, params.lecturePageId));
  const cannotBeCopiedComponentsMeta: CannotBeCopiedComponentsMeta[] = useSelector((state: RootState) => getCannotBeCopiedComponentsMeta(state, state.app.lecturePage.currentLectureId));

  return useCallback(() => {
    const duplicateParam = {
      catalogId: params.catalogId,
      lecturePageId: params.lecturePageId,
      currentCourseIsCollection: currentCourse?.isContentManagementCollection,
    };

    dispatch(duplicateLecturePage(duplicateParam)).then(result => {
      if (!currentCourse?.isContentManagementCollection && result.payload?.id) {
        // Course-level behavior
        lectureLink({
          ...params,
          mode: LecturePageMode.EDIT,
          lecturePageId: (result.payload as LecturePage).id,
        });
      } else if (currentCourse?.isContentManagementCollection && !result.error) {
        // Collection-level behavior
        dispatch(addAlertMessage({
          type: AlertMessageType.INFO,
          message: t.INSTITUTIONS.CONTENT_LIBRARY.COLLECTIONS_HOME.DUPLICATE_STARTED(lecturePage?.title),
        }));
      }

      if (cannotBeCopiedComponentsMeta?.length > 0) {
        const bodyText: JSX.Element = (
          <div>
            {t.LECTURE_PAGES.ADMIN.DUPLICATED.DESCRIPTION(aliases.lectureAliases)}
            <div className='text-left text-medium font-weight-bold ml-6 pl-6 mt-4'>
              <ul className='ml-4 pl-4 mb-0'>
                {cannotBeCopiedComponentsMeta.map((lcMeta, idx) => (
                  <li key={lcMeta.lectureComponentId}>{lcMeta.label}</li>
                ))}
              </ul>
            </div>
          </div>
        );

        dispatch(openConfirmationDialog({
          title: t.LECTURE_PAGES.ADMIN.DUPLICATED.TITLE(courseAliases),
          bodyText,
          confirmButtonVariant: 'primary',
          confirmText: t.FORM.CLOSE(),
          onConfirm: () => { },
          noCancelButton: true,
          icon: 'info',
        }));
      }
    });
  }, [
    params,
    currentCourse?.isContentManagementCollection,
    dispatch,
    lectureLink,
    cannotBeCopiedComponentsMeta,
    aliases.lectureAliases,
    courseAliases,
  ]);
};

export const useDeleteLecture = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();
  const courseOutline = useCourseOutline(params.catalogId);
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const currentInstitutionId = useSelector(state => state.app.currentInstitutionId);
  const { $state, CurrentCourseManager } = useContext(AngularServicesContext);
  const currentCourseLongTeamSet = useSelector(state => getCurrentCourseCourseLongTeamSet(state));
  const currentCourseGroupTeamSet = useSelector(state => getCurrentCourseGroupTeamSet(state));

  const lecturePage = useLecturePageFromParams();
  const lectureName = lecturePage?.title;

  return useCallback(() => {
    dispatch(openConfirmationDialog({
      title: currentCourse?.isContentManagementCollection
        ? t.TIMELINE.DELETE_COLLECTION_LESSON.TITLE()
        : t.TIMELINE.DELETE_LESSON.ARE_YOU_SURE_LECTURE_NAME({ ...courseAliases, lectureName }),
      bodyText: currentCourse?.isContentManagementCollection
        ? t.TIMELINE.DELETE_COLLECTION_LESSON.DESCRIPTION()
        : t.TIMELINE.DELETE_LESSON.DELETE_ALL_SUBMISSIONS({ ...courseAliases, lessonName: lectureName }),
      confirmText: t.FORM.YES_SURE(),
      onConfirm: () => {
        const nearestLecturePageId = getNearestLecture(courseOutline, params.lecturePageId)?.id;
        if (nearestLecturePageId) {
          lectureLink({
            ...params,
            lecturePageId: nearestLecturePageId,
          });
        }

        const deleteParams = {
          catalogId: params.catalogId,
          lecturePageId: params.lecturePageId,
          currentCourseIsCollection: currentCourse?.isContentManagementCollection,
        };
        dispatch(deleteLecturePage(deleteParams)).then(result => {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: t.TIMELINE.DELETE_LESSON.SUCCESS_ALERT(lectureName),
          }));

          if (!currentCourse?.isContentManagementCollection) {
            /**
             * Clearing the courseLongTeamSet/groupTeamSet if the deleted lecture
             * page has the Team/Group Formation LC.
             */
            if (
              !isEmpty(currentCourseLongTeamSet)
              && currentCourseLongTeamSet.lecturePageId === params.lecturePageId
            ) {
              CurrentCourseManager.course.courseLongTeamSet = null;
              dispatch(unsetCurrentCourseCourseLongTeamSet());
            }

            if (
              !isEmpty(currentCourseGroupTeamSet)
              && currentCourseGroupTeamSet.lecturePageId === params.lecturePageId
            ) {
              CurrentCourseManager.course.groupTeamSet = null;
              dispatch(unsetCurrentCourseGroupTeamSet());
            }
          }

          /**
           * Go to course home, if no near lessons found. It is redirected to
           * course home only after the delete to display the data as updated.
           */
          if (!nearestLecturePageId) {
            const stateName = currentCourse?.isContentManagementCollection
              ? 'content-library-collection-home' : 'course-home';
            const stateParams = {
              catalogId: params.catalogId,
              collectionId: currentCourse.id,
              institutionId: currentInstitutionId,
            };
            $state.go(stateName, stateParams);
          }
        });
      },
    }));
  }, [
    $state, CurrentCourseManager.course, courseAliases, courseOutline,
    currentCourseGroupTeamSet, currentCourseLongTeamSet, dispatch, lectureLink,
    lectureName, params, currentCourse?.isContentManagementCollection,
    currentCourse?.id, currentInstitutionId,
  ]);
};

export const useCreateLesson = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const lectureLink = useLecturePageLink();
  const currentCourse = useSelector(getCurrentCourse);
  const lecturePage = useLecturePageFromParams();
  const currentUserId = useSelector(state => state.app.currentUserId);

  return useCallback(() => {
    if (currentCourse?.isContentManagementCollection) {
      dispatch(createCollectionLecturePage({
        title: 'New Lesson',
        folderId: lecturePage?.folderId,
      })).then((response) => {
        if (isEmpty(response?.error)) {
          lectureLink({
            ...params,
            mode: LecturePageMode.EDIT,
            lecturePageId: response.payload.id,
          });
        }
      });
    } else {
      dispatch(createLecturePage({ catalogId: params.catalogId, previousLectureId: params.lecturePageId })).then(actionResult => {
        lectureLink({
          ...params,
          mode: LecturePageMode.EDIT,
          lecturePageId: (actionResult.payload as LecturePage).id,
        });

        // Re-fetch the course timeline with the new lecture page
        dispatch(getFullCourseTimelineWithProgress({
          catalogId: params.catalogId,
          userId: currentUserId,
          outlineOnly: false,
          editMode: true,
        }));
      });
    }
  }, [currentCourse?.isContentManagementCollection, currentUserId, dispatch, lectureLink, lecturePage?.folderId, params]);
};

export const useHideAddUIOnOpen = () => {
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();

  return useCallback(() => {
    if (params.mode === LecturePageMode.EDIT) {
      hideAddUI(dispatch);
    }
  }, [dispatch, params.mode]);
};
